import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";

const isDev = process.env.NODE_ENV !== "production";
const devError = (...args) => {
  if (isDev) {
    // eslint-disable-next-line no-console
    console.error(...args);
  }
};

const ENDPOINT_BY_TAB = {
  applications: "/admin/applications",
  partners: "/admin/partner-inquiries",
  contacts: "/admin/contact-inquiries",
  programs: "/programs",
  packages: "/packages",
};

const DELETE_ENDPOINT_BY_TAB = {
  applications: "/admin/applications",
  partners: "/admin/partner-inquiries",
  contacts: "/admin/contact-inquiries",
  programs: "/admin/programs",
  packages: "/admin/packages",
};

/**
 * Custom hook that owns admin data state:
 * - `stats` for the top counters
 * - `items` for the currently-selected tab
 * - loading indicator
 * - operations: reload, delete, updateStatus
 */
export function useAdminData(tab) {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } catch (err) {
      devError("Failed to load admin stats:", err);
      setError("stats");
    }
  }, []);

  const loadItems = useCallback(async (tabId) => {
    setLoading(true);
    setError(null);
    try {
      const url = ENDPOINT_BY_TAB[tabId];
      const { data } = await api.get(url);
      setItems(data);
    } catch (err) {
      devError("Failed to load items:", err);
      setError("items");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteItem = useCallback(async (tabId, id) => {
    const base = DELETE_ENDPOINT_BY_TAB[tabId];
    await api.delete(`${base}/${id}`);
    await Promise.all([loadItems(tabId), loadStats()]);
  }, [loadItems, loadStats]);

  const updateApplicationStatus = useCallback(async (id, status) => {
    await api.patch(`/admin/applications/${id}`, { status });
    await Promise.all([loadItems("applications"), loadStats()]);
  }, [loadItems, loadStats]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    loadItems(tab);
  }, [tab, loadItems]);

  return { stats, items, loading, error, deleteItem, updateApplicationStatus, reload: () => loadItems(tab) };
}
